import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../Hero.css';
import './VenuesPricing.css';

const PANEL_URL = 'https://panel.tenkusports.com';

const PLANS = [
  { id: 'free', monthly: 0, yearly: 0, features: 6 },
  { id: 'starter', monthly: 49, yearly: 490, features: 11 },
  { id: 'pro', monthly: 149, yearly: 1490, features: 10, highlighted: true },
  { id: 'growth', monthly: 249, yearly: 2490, features: 3, comingSoon: true }
];

const formatPrice = (amount) => `S/${amount.toLocaleString('en-US')}`;

const CheckIcon = () => (
  <svg className="tk-price-check" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 111.42-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z" clipRule="evenodd" />
  </svg>
);

const PlanCard = ({ plan, yearly }) => {
  const { t } = useTranslation();
  const key = `venues.pricing.${plan.id}`;
  const amount = yearly ? Math.ceil(plan.yearly / 12) : plan.monthly;
  const discounted = yearly && plan.monthly > 0;
  const features = Array.from({ length: plan.features }, (_, i) => t(`${key}.f${i + 1}`));

  return (
    <div className={`tk-price-card${plan.highlighted ? ' tk-price-card--highlight' : ''}${plan.comingSoon ? ' tk-price-card--soon' : ''}`}>
      {plan.highlighted && <span className="tk-price-badge">{t('venues.pricing.recommended')}</span>}
      {plan.comingSoon && <span className="tk-price-badge tk-price-badge--soon">{t('venues.pricing.comingSoon')}</span>}
      <h3 className="tk-price-name">{t(`${key}.name`)}</h3>
      <p className="tk-price-tagline">{t(`${key}.tagline`)}</p>
      <div className="tk-price-amount">
        {discounted && <span className="tk-price-was">{formatPrice(plan.monthly)}</span>}
        <span className="tk-price-value">{formatPrice(amount)}</span>
        {plan.monthly > 0 && <span className="tk-price-period">{t('venues.pricing.perMonth')}</span>}
      </div>
      <p className="tk-price-note">
        {plan.monthly === 0
          ? t('venues.pricing.freeForever')
          : discounted
            ? t('venues.pricing.billedYearly', { amount: formatPrice(plan.yearly) })
            : t('venues.pricing.perBranch')}
      </p>
      {plan.comingSoon ? (
        <span className="tk-btn-ghost tk-price-cta tk-price-cta--soon" aria-disabled="true">
          {t('venues.pricing.comingSoon')}
        </span>
      ) : (
        <a
          href={PANEL_URL}
          className={plan.highlighted ? 'tk-btn-primary tk-price-cta' : 'tk-btn-ghost tk-price-cta'}
        >
          {plan.monthly === 0 ? t('venues.pricing.ctaFree') : plan.highlighted ? t('venues.pricing.ctaTrial') : t('venues.pricing.ctaStart')}
        </a>
      )}
      {plan.id !== 'free' && <p className="tk-price-includes">{t(`${key}.includes`)}</p>}
      <ul className="tk-price-features">
        {features.map((feature) => (
          <li key={feature}>
            <CheckIcon />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const VenuesPricing = () => {
  const { t } = useTranslation();
  const [yearly, setYearly] = useState(false);

  return (
    <section id="pricing" className="bg-gray-900 py-20 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('venues.pricing.title')}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('venues.pricing.subtitle')}
          </p>
        </div>

        <div className="tk-price-toggle" role="group" aria-label={t('venues.pricing.billing')}>
          <button
            type="button"
            className={!yearly ? 'is-active' : ''}
            aria-pressed={!yearly}
            onClick={() => setYearly(false)}
          >
            {t('venues.pricing.monthly')}
          </button>
          <button
            type="button"
            className={yearly ? 'is-active' : ''}
            aria-pressed={yearly}
            onClick={() => setYearly(true)}
          >
            {t('venues.pricing.yearly')}
            <span className="tk-price-save">{t('venues.pricing.yearlySave')}</span>
          </button>
        </div>

        <div className="tk-price-grid">
          {PLANS.map((plan) => (
            <PlanCard key={plan.id} plan={plan} yearly={yearly} />
          ))}
        </div>

        <ul className="tk-price-facts">
          <li>{t('venues.pricing.factTrial')}</li>
          <li>{t('venues.pricing.factData')}</li>
          <li>{t('venues.pricing.factPayment')}</li>
        </ul>

        <p className="tk-price-demo">
          {t('venues.pricing.demoQuestion')}{' '}
          <a href="#contact">{t('venues.pricing.demoLink')}</a>
        </p>
      </div>
    </section>
  );
};

export default VenuesPricing;
